import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import dayjs from 'dayjs/esm';

import { isPresent } from 'app/core/util/operators';
import { ApplicationConfigService } from 'app/core/config/application-config.service';
import { createRequestOption } from 'app/core/request/request-util';
import { ICbicContentMst, getCbicContentMstIdentifier } from '../cbic-content-mst.model';
// import { handleHttpError } from 'app/core/interceptor/global-error-handler';


export type EntityResponseType = HttpResponse<ICbicContentMst>;
export type EntityArrayResponseType = HttpResponse<ICbicContentMst[]>;

@Injectable({ providedIn: 'root' })
export class CbicContentMstService {
  protected resourceUrl = this.applicationConfigService.getEndpointFor('api/cbic-content-msts');
  protected contentUrl = this.applicationConfigService.getEndpointFor('api/getContent');
  protected contentwithDocUrl = this.applicationConfigService.getEndpointFor('api/getContentwithDoc');
  protected contentDocUrl = this.applicationConfigService.getEndpointFor('api/getDocument');
  protected refreshUrl = this.applicationConfigService.getEndpointFor('api/refresh');
  protected contentUrlOrderBy = this.applicationConfigService.getEndpointFor('api/getContentByOrder');
  protected ContentByOrderId = this.applicationConfigService.getEndpointFor('api/getTickerContentByOrderId');
  protected ContentBytickerId = this.applicationConfigService.getEndpointFor('api/getTickerData');
  protected newsandmediadocURL = this.applicationConfigService.getEndpointFor('api/getNewsAndMediaFilepath');
  protected newsandmediaUrl = this.applicationConfigService.getEndpointFor('api/getFilepath')
  protected newsAndMediaGallery = this.applicationConfigService.getEndpointFor('api/getFilepathBySize')
  protected fetchTweetsUrl = this.applicationConfigService.getEndpointFor('api/fetch-tweets');
  protected newslettersUrl = this.applicationConfigService.getEndpointFor('api/news-letters');
  protected getContentCount = this.applicationConfigService.getEndpointFor('api/getContentCountByParentId');

  constructor(protected http: HttpClient, protected applicationConfigService: ApplicationConfigService) { }

  create(cbicContentMst: ICbicContentMst): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(cbicContentMst);
    return this.http
      .post<ICbicContentMst>(this.resourceUrl, copy, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  update(cbicContentMst: ICbicContentMst): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(cbicContentMst);
    return this.http
      .put<ICbicContentMst>(`${this.resourceUrl}/${getCbicContentMstIdentifier(cbicContentMst) as number}`, copy, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  partialUpdate(cbicContentMst: ICbicContentMst): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(cbicContentMst);
    return this.http
      .patch<ICbicContentMst>(`${this.resourceUrl}/${getCbicContentMstIdentifier(cbicContentMst) as number}`, copy, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  find(id: number): Observable<EntityResponseType> {
    return this.http
      .get<ICbicContentMst>(`${this.resourceUrl}/${id}`, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  findById(id: string): Observable<EntityResponseType> {
    return this.http
      .get<ICbicContentMst>(`${this.resourceUrl}/${id}`, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  query(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http
      .get<ICbicContentMst[]>(this.resourceUrl, { params: options, observe: 'response' })
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }

  delete(id: number): Observable<HttpResponse<{}>> {
    return this.http.delete(`${this.resourceUrl}/${id}`, { observe: 'response' });
  }

  getContentData(contentType: string): Observable<EntityArrayResponseType> {
    return this.http
      .get<ICbicContentMst[]>(`${this.contentUrl}/${contentType}`, { observe: 'response' })
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
    // .pipe(
    //   catchError(err=>handleHttpError(err))
    // );
  }

  getTickerContentByOrderId(contentType: string): Observable<EntityArrayResponseType> {
    return this.http
      .get<ICbicContentMst[]>(`${this.ContentByOrderId}/${contentType}`, { observe: 'response' })
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
    // .pipe(
    //   catchError(err=>handleHttpError(err))
    // );
  }

  getTickerlatestTenData (contentType: string): Observable<EntityArrayResponseType> {
    return this.http
      .get<ICbicContentMst[]>(`${this.ContentBytickerId}/${contentType}`, { observe: 'response' })
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
    // .pipe(
    //   catchError(err=>handleHttpError(err))
    // );
  }

  getContentDataOrderByDesc(contentType: string): Observable<EntityArrayResponseType> {
    return this.http
      .get<ICbicContentMst[]>(`${this.contentUrlOrderBy}/${contentType}`, { observe: 'response' })
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
    // .pipe(
    //   catchError(err=>handleHttpError(err))
    // );
  }

  getContentRefresh() {
    //alert("calling refresh");
    this.http.get(this.refreshUrl, { observe: 'response' }).subscribe((res: HttpResponse<any>) => {
      if (res) {
        //alert(res);
      }
    });
    // .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
    // .pipe(
    //   catchError(err=>handleHttpError(err))
    // );
  }

  getContentDataWithDoc(contentType: string): Observable<EntityArrayResponseType> {
    return this.http
      .get<ICbicContentMst[]>(`${this.contentwithDocUrl}/${contentType}`, { observe: 'response' })
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }


  // fetch getWhatsNewData data
  getWhatsNewData(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http
      .get<ICbicContentMst[]>(`${this.resourceUrl}/fetchWhatsNew`, { params: options, observe: 'response' })
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }

  // lokesh code for file and image path
  // Shrikant code
  getWhatsNewDataList(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http
      .get<ICbicContentMst[]>(`${this.resourceUrl}/fetchWhatsNewData`, { params: options, observe: 'response' })
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }
  //

  findFilePath(id: number): Observable<EntityArrayResponseType> {
    return this.http
      .get<ICbicContentMst[]>(`${this.resourceUrl}/fetchpath/${id}`, { observe: 'response' })
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }


  gettendersData(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http
      .get<ICbicContentMst[]>(`${this.resourceUrl}/getTenders`, { params: options, observe: 'response' })
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }  

  gettendersDataAll(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http
      .get<ICbicContentMst[]>(`${this.resourceUrl}/getTendersPagess`, { params: options, observe: 'response' })
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }

  getNewsAndMediadocMstData(id?: any,page?: number, size?: number,flag?:any): Observable<any> {
    const options = createRequestOption(id);
    return this.http
      .get<ICbicContentMst[]>(`${this.newsandmediadocURL}/${id}/${page}/${size}/${flag}`)
     
  }
  
  getNewsAndMediaData(id?: any,page?:any,size?:any): Observable<any> {
    const options = createRequestOption(id);
    return this.http
      .get<ICbicContentMst[]>(`${this.newsAndMediaGallery}/${id}/${page}/${size}`)
     
  }

  fetchTweets(): Observable<HttpResponse<string[]>> {
    return this.http
      .get<string[]>(this.fetchTweetsUrl, { observe: 'response' });
      // .pipe(map((res: EntityArrayResponseType) => res));
  }

  getContentNewsLetterData(): Observable<EntityResponseType> {
    return this.http
      .get<ICbicContentMst>(`${this.newslettersUrl}`, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }
   getContentCountByParentId(contentType:number,flag:any): Observable<HttpResponse<number>> {
    return this.http
      .get<number>(`${this.getContentCount}/${contentType}/${flag}`, { observe: 'response' });
      // .pipe(map((res: EntityArrayResponseType) => res));
  }

  addCbicContentMstToCollectionIfMissing(
    cbicContentMstCollection: ICbicContentMst[],
    ...cbicContentMstsToCheck: (ICbicContentMst | null | undefined)[]
  ): ICbicContentMst[] {
    const cbicContentMsts: ICbicContentMst[] = cbicContentMstsToCheck.filter(isPresent);
    if (cbicContentMsts.length > 0) {
      const cbicContentMstCollectionIdentifiers = cbicContentMstCollection.map(
        cbicContentMstItem => getCbicContentMstIdentifier(cbicContentMstItem)!
      );
      const cbicContentMstsToAdd = cbicContentMsts.filter(cbicContentMstItem => {
        const cbicContentMstIdentifier = getCbicContentMstIdentifier(cbicContentMstItem);
        if (cbicContentMstIdentifier == null || cbicContentMstCollectionIdentifiers.includes(cbicContentMstIdentifier)) {
          return false;
        }
        cbicContentMstCollectionIdentifiers.push(cbicContentMstIdentifier);
        return true;
      });
      return [...cbicContentMstsToAdd, ...cbicContentMstCollection];
    }
    return cbicContentMstCollection;
  }

  protected convertDateFromClient(cbicContentMst: ICbicContentMst): ICbicContentMst {
    return Object.assign({}, cbicContentMst, {
      createdDt: cbicContentMst.createdDt?.isValid() ? cbicContentMst.createdDt.toJSON() : undefined,
      updatedDt: cbicContentMst.updatedDt?.isValid() ? cbicContentMst.updatedDt.toJSON() : undefined,
      publishDt: cbicContentMst.publishDt?.isValid() ? cbicContentMst.publishDt.toJSON() : undefined,
      archiveDt: cbicContentMst.archiveDt?.isValid() ? cbicContentMst.archiveDt.toJSON() : undefined,
      contentDt: cbicContentMst.contentDt?.isValid() ? cbicContentMst.contentDt.toJSON() : undefined,
      refDt1: cbicContentMst.refDt1?.isValid() ? cbicContentMst.refDt1.toJSON() : undefined,
      refDt2: cbicContentMst.refDt2?.isValid() ? cbicContentMst.refDt2.toJSON() : undefined,
      expiryDt: cbicContentMst.expiryDt?.isValid() ? cbicContentMst.expiryDt.toJSON() : undefined,
    });
  }

  protected convertDateFromServer(res: EntityResponseType): EntityResponseType {
    if (res.body) {
      res.body.createdDt = res.body.createdDt ? dayjs(res.body.createdDt) : undefined;
      res.body.updatedDt = res.body.updatedDt ? dayjs(res.body.updatedDt) : undefined;
      res.body.publishDt = res.body.publishDt ? dayjs(res.body.publishDt) : undefined;
      res.body.archiveDt = res.body.archiveDt ? dayjs(res.body.archiveDt) : undefined;
      res.body.contentDt = res.body.contentDt ? dayjs(res.body.contentDt) : undefined;
      res.body.refDt1 = res.body.refDt1 ? dayjs(res.body.refDt1) : undefined;
      res.body.refDt2 = res.body.refDt2 ? dayjs(res.body.refDt2) : undefined;
      res.body.expiryDt = res.body.expiryDt ? dayjs(res.body.expiryDt) : undefined;
    }
    return res;
  }

  protected convertDateArrayFromServer(res: EntityArrayResponseType): EntityArrayResponseType {
    if (res.body) {
      res.body.forEach((cbicContentMst: ICbicContentMst) => {
        cbicContentMst.createdDt = cbicContentMst.createdDt ? dayjs(cbicContentMst.createdDt) : undefined;
        cbicContentMst.updatedDt = cbicContentMst.updatedDt ? dayjs(cbicContentMst.updatedDt) : undefined;
        cbicContentMst.publishDt = cbicContentMst.publishDt ? dayjs(cbicContentMst.publishDt) : undefined;
        cbicContentMst.archiveDt = cbicContentMst.archiveDt ? dayjs(cbicContentMst.archiveDt) : undefined;
        cbicContentMst.contentDt = cbicContentMst.contentDt ? dayjs(cbicContentMst.contentDt) : undefined;
        cbicContentMst.refDt1 = cbicContentMst.refDt1 ? dayjs(cbicContentMst.refDt1) : undefined;
        cbicContentMst.refDt2 = cbicContentMst.refDt2 ? dayjs(cbicContentMst.refDt2) : undefined;
        cbicContentMst.expiryDt = cbicContentMst.expiryDt ? dayjs(cbicContentMst.expiryDt) : undefined;
      });
    }
    return res;
  }

  

}
